import { useEffect, useRef, useState } from "react";
import { Loader2, Package, Search, ShieldCheck, Wrench } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { vehicleService } from "@/services/VehicleService";
import { VehicleExtraFilterParams, VehicleExtraOption } from "@/models/vehicle";
import { groupExtrasByType, VehicleExtraGroup } from "@/helpers/vehicleExtras";

type PaymentMethod = "finance" | "cash";

export interface AgentOption {
  value: number;
  label: string;
}

export interface SelectedExtra {
  option: VehicleExtraOption;
  payment: PaymentMethod;
  soldByAgentId: number | null;
  soldByName: string;
}

interface ExtraSelection {
  enabled: boolean;
  optionId: number;
  payment: PaymentMethod;
  soldByAgentId: number | null;
}

interface ChangeExtrasModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Vehicle attributes used to fetch available extras. Null when no vehicle is set. */
  vehicle: VehicleExtraFilterParams | null;
  initialAdminFee: number;
  /** Agents available in the "Sold By" picker. */
  agentOptions: AgentOption[];
  /** Extras already on the quote — used to preselect toggles/products on open. */
  initialExtras?: { description: string; payment: PaymentMethod; soldByAgentId?: number | null }[];
  onConfirm: (result: { adminFee: number; extras: SelectedExtra[] }) => void;
}

const gbp = (n: number) =>
  `£${n.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function typeIcon(typeName: string) {
  const name = typeName.toLowerCase();
  if (name.includes("warranty")) return ShieldCheck;
  if (name.includes("cosmetic") || name.includes("repair") || name.includes("alloy")) return Wrench;
  return Package;
}

function defaultSelection(group: VehicleExtraGroup): ExtraSelection {
  return {
    enabled: false,
    optionId: group.options[0]?.vehicleExtraOptionId ?? 0,
    payment: "finance",
    soldByAgentId: null,
  };
}

/** Searchable single-select for the "Sold By" agent, backed by the shared worker list. */
function SoldBySelect({
  options,
  value,
  onChange,
}: {
  options: AgentOption[];
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-2.5 text-left text-[12px] shadow-xs"
        >
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <span className={cn("flex-1 truncate", !selected && "text-muted-foreground")}>
            {selected ? selected.label : "Search for agent..."}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search for agent..." className="h-9" />
          <CommandList>
            <CommandEmpty>No agent found.</CommandEmpty>
            <CommandGroup>
              {selected && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  Clear selection
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ChangeExtrasModal({ open, onOpenChange, vehicle, initialAdminFee, agentOptions, initialExtras, onConfirm }: ChangeExtrasModalProps) {
  const [adminFee, setAdminFee] = useState(String(initialAdminFee));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<VehicleExtraOption[] | null>(null);
  const [selection, setSelection] = useState<Record<number, ExtraSelection>>({});

  // Refetch whenever the vehicle's values change (make/model/mileage/price/etc.), not just its identity.
  const vehicleRef = useRef(vehicle);
  vehicleRef.current = vehicle;
  const vehicleKey = vehicle ? JSON.stringify(vehicle) : "";

  useEffect(() => {
    if (!open) return;
    setAdminFee(String(initialAdminFee));
    setSelection({});
    setOptions(null);
    setError(null);

    const currentVehicle = vehicleRef.current;
    if (!currentVehicle) return;

    const controller = new AbortController();
    setLoading(true);
    vehicleService
      .getVehicleExtraOptionsFiltered(currentVehicle, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        const opts = data ?? [];
        setOptions(opts);
        // Preselect the extras already on the quote by matching their description.
        const preset: Record<number, ExtraSelection> = {};
        for (const group of groupExtrasByType(opts)) {
          const match = group.options.find((o) =>
            initialExtras?.some((e) => e.description === o.description)
          );
          if (!match) continue;
          const source = initialExtras!.find((e) => e.description === match.description)!;
          preset[group.typeId] = {
            enabled: true,
            optionId: match.vehicleExtraOptionId,
            payment: source.payment,
            soldByAgentId: source.soldByAgentId ?? null,
          };
        }
        setSelection(preset);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError("Failed to load extras. Please try again.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, vehicleKey, initialAdminFee, initialExtras]);

  const groups = groupExtrasByType(options);

  const selFor = (group: VehicleExtraGroup): ExtraSelection => selection[group.typeId] ?? defaultSelection(group);

  const updateSel = (group: VehicleExtraGroup, patch: Partial<ExtraSelection>) =>
    setSelection((prev) => ({
      ...prev,
      [group.typeId]: { ...(prev[group.typeId] ?? defaultSelection(group)), ...patch },
    }));

  const handleConfirm = () => {
    const fee = adminFee === "" ? 0 : Number(adminFee);
    const extras: SelectedExtra[] = [];
    for (const group of groups) {
      const sel = selFor(group);
      if (!sel.enabled) continue;
      const option = group.options.find((o) => o.vehicleExtraOptionId === sel.optionId);
      if (option) {
        const soldByName = agentOptions.find((a) => a.value === sel.soldByAgentId)?.label ?? "";
        extras.push({ option, payment: sel.payment, soldByAgentId: sel.soldByAgentId, soldByName });
      }
    }
    onConfirm({ adminFee: Number.isNaN(fee) ? 0 : fee, extras });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Fees and Add-ons</DialogTitle>
          <DialogDescription>Update the admin fee and select the vehicle's extras.</DialogDescription>
        </DialogHeader>

        {/* Admin fee */}
        <div className="grid grid-cols-[1fr_auto] items-center gap-3">
          <Label htmlFor="admin-fee" className="text-sm text-muted-foreground">
            Admin Fee
          </Label>
          <div className="relative w-40">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              £
            </span>
            <Input
              id="admin-fee"
              type="number"
              min={0}
              className="pl-6 text-right"
              value={adminFee}
              onChange={(e) => setAdminFee(e.target.value)}
            />
          </div>
        </div>

        {/* Extras */}
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-foreground">Extras</span>

          {!vehicle ? (
            <p className="text-[12px] text-muted-foreground">Add a vehicle first to see available extras.</p>
          ) : loading ? (
            <div className="flex items-center gap-2 py-2 text-[12px] text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              Loading extras…
            </div>
          ) : error ? (
            <p className="text-[12px] font-medium text-rose-600">{error}</p>
          ) : groups.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No extras available for this vehicle.</p>
          ) : (
            <div className="flex max-h-80 flex-col gap-3 overflow-y-auto pr-1">
              {groups.map((group) => {
                const sel = selFor(group);
                const Icon = typeIcon(group.typeName);
                const hasChoice = group.options.length > 1;
                return (
                  <div key={group.typeId} className="rounded-lg border border-border bg-card p-3 shadow-sm">
                    {/* Header: icon + name + toggle */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="flex size-7 items-center justify-center rounded-md bg-purple-50 text-purple-700">
                          <Icon className="size-4" />
                        </span>
                        <span className="text-[13px] font-semibold text-foreground">{group.typeName}</span>
                      </div>
                      <Switch
                        checked={sel.enabled}
                        onCheckedChange={(checked) => updateSel(group, { enabled: checked })}
                      />
                    </div>

                    {sel.enabled && (
                      <div className="mt-3 flex flex-col gap-3">
                        {/* Product (only when there is more than one option) */}
                        {hasChoice && (
                          <div className="flex flex-col gap-1">
                            <Label className="text-[12px] font-medium text-foreground">Product</Label>
                            <Select
                              value={String(sel.optionId)}
                              onValueChange={(value) => updateSel(group, { optionId: Number(value) })}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {group.options.map((option) => (
                                  <SelectItem
                                    key={option.vehicleExtraOptionId}
                                    value={String(option.vehicleExtraOptionId)}
                                  >
                                    {option.description} · {gbp(option.price)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Payment method */}
                        <div className="flex flex-col gap-1.5">
                          <Label className="text-[12px] font-medium text-foreground">Payment Method</Label>
                          <div className="flex items-center gap-6">
                            {(["finance", "cash"] as const).map((method) => (
                              <label key={method} className="flex cursor-pointer items-center gap-2 text-[12px]">
                                <input
                                  type="radio"
                                  name={`payment-${group.typeId}`}
                                  checked={sel.payment === method}
                                  onChange={() => updateSel(group, { payment: method })}
                                />
                                <span className="text-foreground">
                                  {method === "finance" ? "Include in Finance" : "Cash"}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Sold By */}
                        <div className="flex flex-col gap-1">
                          <Label className="text-[12px] font-medium text-foreground">Sold By</Label>
                          <SoldBySelect
                            options={agentOptions}
                            value={sel.soldByAgentId}
                            onChange={(agentId) => updateSel(group, { soldByAgentId: agentId })}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 sm:gap-3">
          <DialogClose asChild>
            <Button variant="outline" disabled={loading}>
              Cancel
            </Button>
          </DialogClose>
          <Button className="bg-purple-600 text-white hover:bg-purple-700" onClick={handleConfirm} disabled={loading}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
